export type Stage = 0 | 1 | 2 | 3 | 4;

const styles = `
@keyframes flame1 {
  0%, 100% {
    clip-path: polygon(
      50% 5%, 30% 25%, 25% 50%, 35% 75%,
      50% 95%, 65% 75%, 75% 50%, 60% 25%
    );
  }
  50% {
    clip-path: polygon(
      50% 15%, 35% 30%, 20% 60%, 40% 85%,
      50% 98%, 75% 80%, 80% 55%, 65% 35%
    );
  }
}

@keyframes flame2 {
  0%, 100% {
    clip-path: polygon(
      50% 10%, 35% 35%, 20% 55%, 30% 80%,
      50% 100%, 70% 75%, 80% 55%, 60% 25%
    );
  }
  50% {
    clip-path: polygon(
      50% 5%, 40% 30%, 25% 65%, 35% 85%,
      50% 95%, 65% 75%, 75% 50%, 60% 20%
    );
  }
}

@keyframes inner-flame {
  0%, 100% {
    clip-path: polygon(
      50% 40%, 45% 50%, 35% 65%, 40% 80%,
      50% 90%, 60% 75%, 65% 60%, 55% 50%
    );
  }
  50% {
    clip-path: polygon(
      50% 35%, 40% 45%, 35% 55%, 45% 75%,
      50% 85%, 55% 70%, 60% 60%, 50% 45%
    );
  }
}

@keyframes glow {
  0%, 100% {
    opacity: 0.8;
    transform: scale(0.98);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

.flame-1 {
  animation: flame1 2.8s ease-in-out infinite, glow 2s ease-in-out infinite;
  background: rgba(255, 80, 0, 0.9); /* 赤みがかったオレンジ */
}

.flame-2 {
  animation: flame2 3s ease-in-out infinite, glow 2.5s ease-in-out infinite alternate;
  background: rgba(255, 120, 50, 0.8); /* オレンジ */
}

.inner-flame {
  animation: inner-flame 2.5s ease-in-out infinite, glow 2s ease-in-out infinite;
  background: rgba(255, 200, 0, 0.95); /* 明るい黄色 */
  position: absolute;
  z-index: 3;
}

`;

interface FlameProgressProps {
  stage: Stage;
  size?: number;
}

export const FlameProgress = ({ stage, size = 200 }: FlameProgressProps) => {
  const getStageStyles = () => {
    switch (stage) {
      case 0: // WarmUp（火種）
        return {
          outer: {
            background: 'linear-gradient(to top, #78716c, #57534e, #44403c)',
            boxShadow: '0 0 10px rgba(120, 113, 108, 0.3)'
          },
          inner: {
            background: 'linear-gradient(to top, #92400e, #78716c, #57534e)',
            boxShadow: '0 0 8px rgba(146, 64, 14, 0.3)'
          },
          core: {
            background: 'linear-gradient(to top, #ea580c, #92400e, #78716c)',
            boxShadow: '0 0 5px rgba(234, 88, 12, 0.3)'
          },
          label: 'WarmUp'
        };
      case 1: // Kickoff（不完全燃焼）
        return {
          outer: {
            background: 'linear-gradient(to top, #ef4444, #dc2626, #991b1b)',
            boxShadow: '0 0 20px rgba(239, 68, 68, 0.5)'
          },
          inner: {
            background: 'linear-gradient(to top, #fb923c, #ea580c, #ef4444)',
            boxShadow: '0 0 15px rgba(251, 146, 60, 0.5)'
          },
          core: {
            background: 'linear-gradient(to top, #fde047, #facc15, #fb923c)',
            boxShadow: '0 0 10px rgba(253, 224, 71, 0.5)'
          },
          label: 'Kickoff'
        };
      case 2: // Build-Up（遷移初期）
        return {
          outer: {
            background: 'linear-gradient(to top, #fb923c, #ea580c, #9a3412)',
            boxShadow: '0 0 20px rgba(251, 146, 60, 0.5)'
          },
          inner: {
            background: 'linear-gradient(to top, #fde047, #facc15, #ea580c)',
            boxShadow: '0 0 15px rgba(253, 224, 71, 0.5)'
          },
          core: {
            background: 'linear-gradient(to top, #fef9c3, #fde047, #facc15)',
            boxShadow: '0 0 10px rgba(254, 249, 195, 0.5)'
          },
          label: 'BuildUp'
        };
      case 3: // Finishing（遷移後期）
        return {
          outer: {
            background: 'linear-gradient(to top, #60a5fa, #3b82f6, #1d4ed8)',
            boxShadow: '0 0 20px rgba(96, 165, 250, 0.5)'
          },
          inner: {
            background: 'linear-gradient(to top, #fde047, #93c5fd, #60a5fa)',
            boxShadow: '0 0 10px rgba(253, 224, 71, 0.5)'
          },
          core: {
            background: 'linear-gradient(to top, #fef9c3, #fde047, #facc15)',
            boxShadow: '0 0 10px rgba(254, 249, 195, 0.5)'
          },
          label: 'Finishing'
        };
      case 4: // Goal（完全燃焼）
        return {
          outer: {
            background: 'linear-gradient(to top, #2563eb, #1d4ed8, #1e40af)',
            boxShadow: '0 0 20px rgba(37, 99, 235, 0.5)'
          },
          inner: {
            background: 'linear-gradient(to top, #60a5fa, #2563eb, #1d4ed8)',
            boxShadow: '0 0 15px rgba(96, 165, 250, 0.5)'
          },
          core: {
            background: 'linear-gradient(to top, #bfdbfe, #60a5fa, #2563eb)',
            boxShadow: '0 0 10px rgba(191, 219, 254, 0.5)'
          },
          label: 'Goal'
        };
    }
  };

  const stageStyles = getStageStyles();
  const flameWidth = size * 0.8; // 炎の幅を高さに対して調整

  return (
    <div>
      <style>{styles}</style>
      <div className="relative flex items-center justify-center" style={{ height: size }}>
        {stage === 0 ? (
          <>
            {/* 内側の黄色い炎 */}
            <div
              className="absolute inner-flame"
              style={{
                ...stageStyles.core,
                width: flameWidth * 0.9,
                height: size * 0.9
              }}
            />
          </>
        ) : (
          <>
            {/* 外側の赤い炎 */}
            <div
              className="absolute flame-1"
              style={{
                ...stageStyles.outer,
                width: flameWidth,
                height: size
              }}
            />
            {/* 中間のオレンジの炎 */}
            <div
              className="absolute flame-2"
              style={{
                ...stageStyles.inner,
                width: flameWidth,
                height: size
              }}
            />
            {/* 内側の黄色い炎 */}
            <div
              className="absolute inner-flame"
              style={{
                ...stageStyles.core,
                width: flameWidth * 0.9,
                height: size * 0.9
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

interface BurnIngicatorProps {
  stage: Stage;
}

export const BurnIngicatorLabel = ({ stage }: BurnIngicatorProps) => {
  return (
    <div className="font-footballlog italic font-semibold text-lg">
      {stage === 0 && <div className="text-stone-600">WARM-UP</div>}
      {stage === 1 && <div className="text-red-500">KICKOFF</div>}
      {stage === 2 && <div className="text-orange-400">BUILD-UP</div>}
      {stage === 3 && <div className="text-blue-400">FINISHING</div>}
      {stage === 4 && <div className="text-blue-700">GOAL</div>}
    </div>
  );
};

export const BurnIngicator = ({ stage }: BurnIngicatorProps) => {
  // ステージに応じたスタイルとグラデーション
  const getStageStyles = () => {
    switch (stage) {
      case 0:
        return {
          width: 'w-0',
          gradient: 'bg-gradient-to-r from-stone-600 via-stone-500 to-stone-400',
          shadow: 'shadow-stone-500/50'
        };
      case 1:
        return {
          width: 'w-1/4',
          gradient: 'bg-gradient-to-r from-red-600 via-orange-300 to-red-500',
          shadow: 'shadow-red-500/50'
        };
      case 2:
        return {
          width: 'w-2/4',
          gradient: 'bg-gradient-to-r from-red-500 via-yellow-300 to-orange-300',
          shadow: 'shadow-orange-500/50'
        };
      case 3:
        return {
          width: 'w-3/4',
          gradient: 'bg-gradient-to-r from-yellow-300 via-blue-200 to-blue-500',
          shadow: 'shadow-blue-400/50'
        };
      case 4:
        return {
          width: 'w-full',
          gradient: 'bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600',
          shadow: 'shadow-blue-500/50'
        };
    }
  };

  const stageStyles = getStageStyles();

  return (
    <div className="relative w-full">
      <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden shadow-sm">
        {/* ベースとなるグロー効果 */}
        <div className="absolute inset-0 bg-gradient-to-r from-white to-white" />

        {/* プログレスバー本体 */}
        <div
          className={`
            absolute top-0 left-0 h-full rounded-full
            ${stageStyles.width}
            ${stageStyles.gradient}
            transition-all duration-1000 ease-in-out
            shadow-lg ${stageStyles.shadow}
          `}>
          {/* アニメーションされるグロー効果 */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-pulse" />
        </div>
      </div>

      {/* ステージ表示 */}
      <div className="absolute -bottom-1 right-2 mt-2 flex justify-between text-xs text-gray-600">
        <div className="flex items-center">
          <BurnIngicatorLabel stage={stage} />
          <div className="relative -top-1 ms-3">
            <FlameProgress stage={stage} size={30} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BurnIngicator;
